import type { ReactNode } from 'react';
import { createContext, useState } from 'react';
import authImplementation from '../auth/auth';
import type { Auth, ForgotPayload, HelpPayload, SignInPayload } from '../../types';
import { STORAGE_KEY, config } from '../config';
import { getJSONItem } from '../services/storage/storage';

export const AuthContext = createContext<Auth | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<string | null>(authImplementation?.user?.username ?? '');
  const [currentEmail, setCurrentEmail] = useState<string | null>(null);
  const [currentCf, setCurrentCf] = useState<string | null>(null);

  const signin = ({ username, password }: SignInPayload, callbackSuccess: VoidFunction, callbackFail: VoidFunction) => {
    return authImplementation.signin(
      { username, password },
      () => {
        setCurrentUser(username);
        callbackSuccess();
      },
      () => {
        console.error('login error');
        callbackFail();
      },
    );
  };

  const signout = (callbackSuccess: VoidFunction) => {
    return authImplementation.signout(() => {
      setCurrentUser(null);
      callbackSuccess();
    });
  };

  const forgot = (
    { email, cf }: ForgotPayload,
    callbackSuccess: VoidFunction,
    callbackFail: (errorType: number) => void,
  ) => {
    return authImplementation.forgot(
      { email, cf },
      () => {
        setCurrentEmail(email);
        setCurrentCf(cf);
        callbackSuccess();
      },
      (errorType) => {
        console.error('forgot error');
        callbackFail(errorType);
      },
    );
  };

  const help = (
    { fullName, email, subject, message }: HelpPayload,
    callbackSuccess: VoidFunction,
    callbackFail: VoidFunction,
  ) => {
    return authImplementation.help(
      { fullName, email, subject, message },
      () => {
        callbackSuccess();
      },
      () => {
        console.error('help error');
        callbackFail();
      },
    );
  };

  const forgotSms = (
    { email, cf }: ForgotPayload,
    callbackSuccess: VoidFunction,
    callbackFail: (errorType: number) => void,
  ) => {
    return authImplementation.forgotSms(
      { email, cf },
      () => {
        setCurrentEmail(email);
        setCurrentCf(cf);
        callbackSuccess();
      },
      (errorType) => {
        console.error('forgot error');
        callbackFail(errorType);
      },
    );
  };

  const isAuthenticated = getJSONItem(STORAGE_KEY.USER, {})?.isAuthenticated;

  const providerValue = {
    user: {
      ...authImplementation.user,
      ...(currentUser && { username: currentUser }),
      ...(currentEmail && { email: currentEmail }),
      ...(currentCf && { cf: currentCf }),
    },
    signin,
    signout,
    forgot,
    forgotSms,
    help,
    isAuthenticated,
    abtest: config.abtest,
  };

  return <AuthContext.Provider value={providerValue}>{children}</AuthContext.Provider>;
};
